import { AxiosResponse, AxiosError } from 'axios';
import keyBy from 'lodash.keyby';

import {
    EntityUuid,
    Entity,
    Entities,
    EntityError,
    EntitiesSaveSucceededAction,
    EntitiesSaveRequestedAction,
    EntitiesSaveFailedAction,
    EntitiesUpdateAction,
    EntityUpdateAction,
    EntitiesUpdatesPurgeAction,
    EntitiesValueUpdateActionPayload,
    EntitiesValueUpdateAction,
    EntityValueUpdateActionPayload,
    EntityValueUpdateAction,
    EntityRemoveAction,
    EntitiesRemoveAction,
    EntitySetAction,
    EntitiesSetAction,
    EntitiesMergeAction,
    EntityErrorsSetAction
} from './types';
import {
    ENTITIES_SAVE_REQUESTED,
    ENTITIES_SAVE_SUCCEEDED,
    ENTITIES_SAVE_FAILED,
    ENTITIES_UPDATE,
    ENTITY_UPDATE,
    ENTITIES_VALUE_UPDATE,
    ENTITY_VALUE_UPDATE,
    ENTITY_REMOVE,
    ENTITIES_REMOVE,
    ENTITIES_UPDATES_PURGE,
    ENTITY_SET,
    ENTITIES_SET,
    ENTITIES_MERGE,
    ENTITIES_ERRORS_SET
} from './constants';

/**
 * Intended to be called from either a redux-sagas or component.
 */
export function requestSaveEntities(): EntitiesSaveRequestedAction {
    return {
        type: ENTITIES_SAVE_REQUESTED
    };
}

/**
 * Only intended for use in redux-sagas.
 */
export function succeededSaveEntities(
    response: AxiosResponse
): EntitiesSaveSucceededAction {
    return {
        type: ENTITIES_SAVE_SUCCEEDED,
        payload: response
    };
}

/**
 * Only intended for use in redux-sagas.
 */
export function failedSaveEntities(
    error: AxiosError
): EntitiesSaveFailedAction {
    return {
        type: ENTITIES_SAVE_FAILED,
        payload: error,
        error: true
    };
}

/**
 * Intended to be called from a component.
 */
export function updateEntities(entities: Entity[]): EntitiesUpdateAction {
    return {
        type: ENTITIES_UPDATE,
        payload: keyBy(entities, 'uuid')
    };
}

/**
 * Intended to be called from a component.
 */
export function updateEntity(entity: Entity): EntityUpdateAction {
    return {
        type: ENTITY_UPDATE,
        payload: entity
    };
}

/**
 * Intended to be called from a component.
 */
export function updateEntityValue({
    entityUuid,
    field,
    value
}: EntityValueUpdateActionPayload): EntityValueUpdateAction {
    return {
        type: ENTITY_VALUE_UPDATE,
        payload: {
            entityUuid,
            field,
            value
        }
    };
}

/**
 * Intended to be called from a component.
 */
export function updateEntitiesValue({
    entityUuids,
    field,
    value
}: EntitiesValueUpdateActionPayload): EntitiesValueUpdateAction {
    return {
        type: ENTITIES_VALUE_UPDATE,
        payload: {
            entityUuids,
            field,
            value
        }
    };
}

/**
 * Intended to be called from a component.
 */
export function removeEntity(entityUuid: EntityUuid): EntityRemoveAction {
    return {
        type: ENTITY_REMOVE,
        payload: entityUuid
    };
}

/**
 * Intended to be called from a component.
 */
export function removeEntities(
    entityUuids: EntityUuid[]
): EntitiesRemoveAction {
    return {
        type: ENTITIES_REMOVE,
        payload: entityUuids
    };
}

/**
 * Intended to be called from a component.
 */
export function purgeEntitiesUpdates(): EntitiesUpdatesPurgeAction {
    return {
        type: ENTITIES_UPDATES_PURGE
    };
}

/**
 * Only intended for use in redux-sagas.
 */
export function setEntity(entity: Entity): EntitySetAction {
    return {
        type: ENTITY_SET,
        payload: entity
    };
}

/**
 * Only intended for use in redux-sagas.
 */
export function setEntities(entities: Entities): EntitiesSetAction {
    return {
        type: ENTITIES_SET,
        payload: entities
    };
}

/**
 * Only intended for use in redux-sagas.
 * @param entities
 */
export function mergeEntities(entities: Entities): EntitiesMergeAction {
    return {
        type: ENTITIES_MERGE,
        payload: entities
    };
}

/**
 * Only intended for use in redux-sagas.
 */
export function setEntityErrors(
    entityErrors: EntityError[]
): EntityErrorsSetAction {
    return {
        type: ENTITIES_ERRORS_SET,
        payload: entityErrors
    };
}
